import { createContext, useContext, useState, useEffect, useCallback, useMemo, ReactNode, useRef } from 'react';
import { apiClient } from '@/api/axios';
import { useAuth } from '@/context/AuthContext';

export type OwnerOnboardingStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED';

export interface OwnerOnboardingState {
  status: OwnerOnboardingStatus;
  completedSections: {
    basicInformation: boolean; // Name, Email, Contact Number
  };
}

interface ApiUserProfile {
  id: string;
  clerkId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  phone?: string;
}

interface OwnerOnboardingContextType {
  onboardingState: OwnerOnboardingState;
  updateOnboardingSection: (section: keyof OwnerOnboardingState['completedSections'], isComplete: boolean) => void;
  isOnboardingComplete: boolean;
  getOnboardingProgress: () => { completed: number; total: number; percentage: number };
  forceCompleteOnboarding: () => void;
  resetOnboarding: () => void;
  isCheckingProfile: boolean;
}

const OwnerOnboardingContext = createContext<OwnerOnboardingContextType | undefined>(undefined);

const DEFAULT_STATE: OwnerOnboardingState = {
  status: 'NOT_STARTED',
  completedSections: {
    basicInformation: false,
  },
};

export function OwnerOnboardingProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const hasCheckedProfileRef = useRef(false);

  const [onboardingState, setOnboardingState] = useState<OwnerOnboardingState>(DEFAULT_STATE);
  // Start as checking=true so sidebar stays unlocked until DB check finishes
  const [isCheckingProfile, setIsCheckingProfile] = useState(true);

  // Always check the database for profile completeness
  useEffect(() => {
    const checkProfileInDatabase = async () => {
      if (!isAuthenticated || !user || hasCheckedProfileRef.current) {
        return;
      }

      hasCheckedProfileRef.current = true;
      setIsCheckingProfile(true);

      try {
        const response = await apiClient.get<ApiUserProfile>('/users/profile');
        const profileData = response.data;

        const hasName = !!(
          profileData.fullName ||
          (profileData.firstName && profileData.firstName.trim()) ||
          (profileData.lastName && profileData.lastName.trim())
        );
        const hasEmail = !!(profileData.email && profileData.email.trim());
        const hasPhone = !!(profileData.phone && profileData.phone.trim());

        const basicInfoComplete = hasName && hasEmail && hasPhone;

        setOnboardingState({
          status: basicInfoComplete ? 'COMPLETED' : 'NOT_STARTED',
          completedSections: {
            basicInformation: basicInfoComplete,
          },
        });
      } catch (error) {
        console.error('Failed to check profile in database:', error);
      } finally {
        setIsCheckingProfile(false);
      }
    };

    checkProfileInDatabase();
  }, [isAuthenticated, user]);

  const isOnboardingComplete = onboardingState.completedSections.basicInformation;

  const updateOnboardingSection = useCallback((
    section: keyof OwnerOnboardingState['completedSections'],
    isComplete: boolean
  ) => {
    setOnboardingState(prev => {
      if (prev.completedSections[section] === isComplete) {
        return prev;
      }

      const newSections = {
        ...prev.completedSections,
        [section]: isComplete,
      };
      const allComplete = Object.values(newSections).every(Boolean);

      return {
        completedSections: newSections,
        status: allComplete ? 'COMPLETED' : 'IN_PROGRESS',
      };
    });
  }, []);

  const getOnboardingProgress = useCallback(() => {
    const sections = Object.values(onboardingState.completedSections);
    const completed = sections.filter(Boolean).length;
    const total = sections.length;
    const percentage = Math.round((completed / total) * 100);
    return { completed, total, percentage };
  }, [onboardingState.completedSections]);

  const forceCompleteOnboarding = useCallback(() => {
    setOnboardingState({
      status: 'COMPLETED',
      completedSections: {
        basicInformation: true,
      },
    });
  }, []);

  const resetOnboarding = useCallback(() => {
    setOnboardingState(DEFAULT_STATE);
  }, []);

  // Reset check flag when user changes (logout/login)
  useEffect(() => {
    if (!isAuthenticated) {
      hasCheckedProfileRef.current = false;
      setOnboardingState(DEFAULT_STATE);
      setIsCheckingProfile(true);
    }
  }, [isAuthenticated]);

  const contextValue = useMemo(() => ({
    onboardingState,
    updateOnboardingSection,
    isOnboardingComplete,
    getOnboardingProgress,
    forceCompleteOnboarding,
    resetOnboarding,
    isCheckingProfile,
  }), [onboardingState, updateOnboardingSection, isOnboardingComplete, getOnboardingProgress, forceCompleteOnboarding, resetOnboarding, isCheckingProfile]);

  return (
    <OwnerOnboardingContext.Provider value={contextValue}>
      {children}
    </OwnerOnboardingContext.Provider>
  );
}

export function useOwnerOnboarding() {
  const context = useContext(OwnerOnboardingContext);
  if (!context) {
    throw new Error('useOwnerOnboarding must be used within OwnerOnboardingProvider');
  }
  return context;
}
